import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import "./LanguageSelector.css";

export default function LanguageSelector() {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const selectorRef = useRef(null);
  const touchHandledRef = useRef(false);

  const currentLanguage = supportedLanguages.find((lang) => lang.code === language);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = useCallback(
    (code) => {
      setLanguage(code);
      setOpen(false);
    },
    [setLanguage],
  );

  return (
    <div
      className="language-selector notranslate"
      ref={selectorRef}
      data-current-language={currentLanguage?.code ?? "en"}
      translate="no"
    >
      <button
        type="button"
        className="language-selector__toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Globe size={18} />
        <span className="language-selector__label">
          {currentLanguage?.flag} {currentLanguage?.label ?? "English"}
        </span>
        <ChevronDown size={16} className={`language-selector__chevron ${open ? "open" : ""}`} />
      </button>

      <ul
        className={`language-selector__list ${open ? "visible" : ""}`}
        role="listbox"
        aria-activedescendant={currentLanguage?.code}
      >
        {supportedLanguages.map((lang) => (
          <li key={lang.code}>
            <button
              type="button"
              className={`language-selector__option ${language === lang.code ? "active" : ""}`}
              onTouchStart={(event) => {
                touchHandledRef.current = true;
                event.preventDefault();
                handleLanguageSelect(lang.code);
              }}
              onClick={(event) => {
                if (touchHandledRef.current) {
                  touchHandledRef.current = false;
                  return;
                }
                event.preventDefault();
                handleLanguageSelect(lang.code);
              }}
            >
              <span className="language-selector__flag" aria-hidden="true">
                {lang.flag}
              </span>
              <div className="language-selector__text">
                <span>{lang.label}</span>
                {lang.englishLabel && <small>{lang.englishLabel}</small>}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

